import { useState, useEffect, useRef } from 'react';
import { patientDashboardAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Users, Search, Heart, Activity, BedDouble, Clock, ArrowUpRight,
  ArrowDownRight, X, FileText, Pill, Stethoscope, CalendarDays,
  Phone, Droplets, Thermometer, Wind, ChevronRight, AlertCircle,
  ShieldAlert, Database, Loader2,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { BodyMap } from '../components/BodyMap';

// ========== SPARKLINE ==========
const Spark = ({ data, color = '#10b981', h = 24 }) => {
  if (!data?.length || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - ((v - min) / range) * 80}`).join(' ');
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height: h }} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ========== DONUT ==========
// SVG arc-path donut — robust across browsers (no CSS transform-origin quirks).
const Donut = ({ data, size = 120 }) => {
  const visible = data.filter(d => d.value > 0);
  const total = visible.reduce((s, d) => s + d.value, 0) || 1;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = (size - 6) / 2;
  const rInner = rOuter - 18;

  // For a single 100% slice (full circle) we can't draw an arc — render an annulus instead.
  const onlyOne = visible.length === 1;

  const polarToCartesian = (r, angleRad) => ({
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  });

  // Start at 12 o'clock (-PI/2) and sweep clockwise.
  let cursor = -Math.PI / 2;

  return (
    <div className="flex items-center gap-5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background track */}
        <circle cx={cx} cy={cy} r={(rOuter + rInner) / 2} fill="none" stroke="#f1f5f9" strokeWidth={rOuter - rInner} />
        {onlyOne ? (
          <circle cx={cx} cy={cy} r={(rOuter + rInner) / 2} fill="none" stroke={visible[0].color} strokeWidth={rOuter - rInner} />
        ) : (
          visible.map((d, i) => {
            const pct = d.value / total;
            const startAngle = cursor;
            const endAngle = cursor + pct * 2 * Math.PI;
            cursor = endAngle;
            const largeArc = pct > 0.5 ? 1 : 0;
            const p1 = polarToCartesian(rOuter, startAngle);
            const p2 = polarToCartesian(rOuter, endAngle);
            const p3 = polarToCartesian(rInner, endAngle);
            const p4 = polarToCartesian(rInner, startAngle);
            const pathD = [
              `M ${p1.x} ${p1.y}`,
              `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${p2.x} ${p2.y}`,
              `L ${p3.x} ${p3.y}`,
              `A ${rInner} ${rInner} 0 ${largeArc} 0 ${p4.x} ${p4.y}`,
              'Z',
            ].join(' ');
            return <path key={i} d={pathD} fill={d.color} stroke="#fff" strokeWidth="1" />;
          })
        )}
        <text x="50%" y="50%" textAnchor="middle" dy="0.3em" className="text-base font-bold fill-slate-700">{total}</text>
      </svg>
      <div className="space-y-1.5">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: d.color }} />
            <span className="text-slate-500">{d.label}</span>
            <span className="font-bold text-slate-700">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ========== HORIZONTAL BAR ==========
const HBar = ({ data }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="space-y-2">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-slate-600 w-28 truncate text-right">{d.name}</span>
          <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-700" style={{ width: `${(d.count / max) * 100}%` }} />
          </div>
          <span className="text-xs font-bold text-slate-700 w-8">{d.count}</span>
        </div>
      ))}
    </div>
  );
};

// ========== VITAL CARD ==========
const VitalCard = ({ label, value, unit, icon: Icon, color, trend, status, testId }) => {
  const isNormal = status === 'normal';
  return (
    <div className={cn("bg-white rounded-xl border p-4 hover:shadow-md transition-all", isNormal ? "border-slate-200" : "border-amber-300")} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", color)}>
          <Icon size={18} className="text-white" />
        </div>
        <span className={cn("text-[9px] px-2 py-0.5 rounded-full font-bold",
          isNormal ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
        )}>{isNormal ? 'Normal' : 'Abnormal'}</span>
      </div>
      <p className="text-xs text-slate-400 font-medium uppercase">{label}</p>
      <p className="text-xl font-bold text-slate-900 mt-0.5">{value ?? '--'} <span className="text-xs font-normal text-slate-400">{unit}</span></p>
      {trend?.length > 1 && <div className="mt-1.5 opacity-70"><Spark data={trend} color={isNormal ? '#10b981' : '#f59e0b'} /></div>}
    </div>
  );
};

// ========== RISK SCORE BADGE ==========
const RiskScoreBadge = ({ score, level }) => {
  const config = {
    critical: { bg: 'bg-red-500', ring: 'ring-red-200', text: 'text-white', label: 'CRITICAL' },
    high: { bg: 'bg-orange-500', ring: 'ring-orange-200', text: 'text-white', label: 'HIGH' },
    moderate: { bg: 'bg-amber-400', ring: 'ring-amber-200', text: 'text-amber-900', label: 'MODERATE' },
    low: { bg: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-white', label: 'LOW' },
  };
  const c = config[level] || config.low;
  return (
    <div className="flex items-center gap-3" data-testid="risk-score-badge">
      <div className={cn('h-14 w-14 rounded-full flex items-center justify-center ring-4 shadow-lg', c.bg, c.ring)}>
        <span className={cn('text-lg font-bold', c.text)}>{score}</span>
      </div>
      <div>
        <p className="text-xs text-slate-400 uppercase font-medium">Risk Score</p>
        <p className={cn('text-sm font-bold', level === 'critical' ? 'text-red-600' : level === 'high' ? 'text-orange-600' : level === 'moderate' ? 'text-amber-600' : 'text-emerald-600')}>
          {c.label}
        </p>
      </div>
    </div>
  );
};

// ========== RISK FACTORS PANEL ==========
const RiskFactors = ({ factors }) => {
  if (!factors?.length) return null;
  const grouped = {};
  factors.forEach(f => {
    const cat = f.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(f);
  });
  const catLabels = { demographics: 'Demographics', conditions: 'Conditions', vitals: 'Vitals', diagnoses: 'Diagnoses', allergies: 'Allergies', admissions: 'Admissions' };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="risk-factors-panel">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><ShieldAlert size={14} /> Risk Factors</h3>
      <div className="space-y-3 max-h-52 overflow-y-auto">
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat}>
            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">{catLabels[cat] || cat}</p>
            {items.map((f, i) => (
              <div key={i} className="flex items-center justify-between py-1 px-2 rounded bg-slate-50 mb-1">
                <span className="text-[11px] text-slate-600">{f.factor}</span>
                <span className="text-[10px] font-bold text-red-500">+{f.points}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ========== PATIENT SNAPSHOT (Quick at-a-glance card for Front Desk / Billing) ==========
const PatientSnapshot = ({ patient: p, latestVitals: lv, diagnoses, prescriptions, upcoming, totalVisits, riskData }) => {
  // Compose snapshot data from already-loaded patient detail (no extra API)
  const nextAppt = upcoming?.[0];
  const lastDiagnosis = diagnoses?.[0];
  const activeRxCount = prescriptions?.filter(rx => {
    if (!rx.follow_up_date) return false;
    const fu = new Date(rx.follow_up_date);
    return !isNaN(fu) && fu >= new Date();
  }).length || prescriptions?.length || 0;
  const bpAbnormal = lv?.blood_pressure_systolic && (lv.blood_pressure_systolic > 140 || lv.blood_pressure_systolic < 90);
  const o2Abnormal = lv?.oxygen_saturation && lv.oxygen_saturation < 95;
  const tempAbnormal = lv?.temperature && (lv.temperature > 37.5 || lv.temperature < 36);
  const alerts = [];
  if (p.allergies?.length) alerts.push({ icon: AlertCircle, text: `${p.allergies.length} allergy alert${p.allergies.length > 1 ? 's' : ''}`, color: 'text-amber-600 bg-amber-50' });
  if (p.chronic_conditions?.length) alerts.push({ icon: Activity, text: `${p.chronic_conditions.length} chronic condition${p.chronic_conditions.length > 1 ? 's' : ''}`, color: 'text-rose-600 bg-rose-50' });
  if (bpAbnormal || o2Abnormal || tempAbnormal) alerts.push({ icon: Heart, text: 'Abnormal vitals on last visit', color: 'text-red-600 bg-red-50' });
  if (riskData?.risk_level === 'high' || riskData?.risk_level === 'critical') alerts.push({ icon: ShieldAlert, text: `Risk: ${riskData.risk_level}`, color: 'text-red-600 bg-red-50' });

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="patient-snapshot">
      <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
        <Activity size={14} className="text-teal-600" /> Patient Snapshot
      </h3>

      {/* Top: 3 KPIs */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-lg p-3 border border-teal-100">
          <p className="text-[10px] uppercase font-semibold text-teal-600 tracking-wide">Total Visits</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{totalVisits || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100">
          <p className="text-[10px] uppercase font-semibold text-blue-600 tracking-wide">Active Rx</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{activeRxCount}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-100">
          <p className="text-[10px] uppercase font-semibold text-purple-600 tracking-wide">Diagnoses</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{diagnoses?.length || 0}</p>
        </div>
      </div>

      {/* Next Appointment */}
      <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
        <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wide mb-1 flex items-center gap-1.5">
          <CalendarDays size={11} /> Next Appointment
        </p>
        {nextAppt ? (
          <div>
            <p className="text-sm font-semibold text-slate-800">
              {nextAppt.appointment_date || nextAppt.date} {nextAppt.time_slot && <span className="text-slate-500 font-normal">· {nextAppt.time_slot}</span>}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">Dr. {nextAppt.doctor_name} {nextAppt.appointment_type && <span>· {nextAppt.appointment_type.replace('_', ' ')}</span>}</p>
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">No upcoming appointment booked</p>
        )}
      </div>

      {/* Last Diagnosis */}
      <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
        <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wide mb-1 flex items-center gap-1.5">
          <Stethoscope size={11} /> Last Diagnosis
        </p>
        {lastDiagnosis ? (
          <div>
            <p className="text-sm font-semibold text-slate-800">{lastDiagnosis.diagnosis_name}</p>
            <p className="text-xs text-slate-500 mt-0.5">{lastDiagnosis.diagnosis_type} · Dr. {lastDiagnosis.doctor_name}</p>
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">No diagnoses on record</p>
        )}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wide mb-1">Alerts</p>
          {alerts.map((a, i) => (
            <div key={i} className={cn('flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium', a.color)}>
              <a.icon size={12} />
              <span>{a.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== PATIENT DETAIL VIEW ==========
const PatientDetail = ({ patientId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [riskData, setRiskData] = useState(null);
  const [bodyMapData, setBodyMapData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [detailRes, riskRes, bodyRes] = await Promise.all([
          patientDashboardAPI.detail(patientId),
          patientDashboardAPI.riskScore(patientId).catch(() => null),
          patientDashboardAPI.bodyMap(patientId).catch(() => null),
        ]);
        setData(detailRes.data);
        if (riskRes?.data) setRiskData(riskRes.data);
        if (bodyRes?.data) setBodyMapData(bodyRes.data);
      } catch { toast.error('Failed to load patient'); }
      setLoading(false);
    })();
  }, [patientId]);

  if (loading) return <div className="p-8 text-center text-slate-400">Loading patient data...</div>;
  if (!data) return null;

  const { patient: p, latest_vitals: lv, vitals_trends: vt, diagnoses, lab_reports, prescriptions, upcoming_appointments, assigned_doctors, visit_trend, admission_history } = data;

  const bpStatus = (lv?.blood_pressure_systolic && (lv.blood_pressure_systolic > 140 || lv.blood_pressure_systolic < 90)) ? 'abnormal' : 'normal';
  const hrStatus = (lv?.pulse_rate && (lv.pulse_rate > 100 || lv.pulse_rate < 60)) ? 'abnormal' : 'normal';
  const bsStatus = (lv?.blood_sugar && (lv.blood_sugar > 140 || lv.blood_sugar < 70)) ? 'abnormal' : 'normal';
  const tempStatus = (lv?.temperature && (lv.temperature > 37.5 || lv.temperature < 36)) ? 'abnormal' : 'normal';
  const o2Status = (lv?.oxygen_saturation && lv.oxygen_saturation < 95) ? 'abnormal' : 'normal';

  return (
    <div className="space-y-6 animate-in fade-in-0 duration-300" data-testid="patient-detail-view">
      {/* Patient Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {p.first_name?.charAt(0)}{p.last_name?.charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{p.name}</h2>
              <p className="text-sm text-slate-500">UHID: {p.patient_id} &middot; {p.age ? `${p.age} yrs` : ''} {p.gender ? `/ ${p.gender}` : ''}</p>
              {p.phone && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Phone size={11} />{p.phone}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        {/* Risk Score */}
        {riskData && (
          <div className="mt-4 mb-2">
            <RiskScoreBadge score={riskData.risk_score} level={riskData.risk_level} />
          </div>
        )}
        {/* Quick info */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-4 text-xs">
          {[
            { label: 'Blood Group', value: p.blood_group || '--' },
            { label: 'DOB', value: p.date_of_birth?.substring(0, 10) || '--' },
            { label: 'Height', value: lv?.height ? `${lv.height} cm` : '--' },
            { label: 'Weight', value: lv?.weight ? `${lv.weight} kg` : '--' },
            { label: 'BMI', value: lv?.bmi ? lv.bmi.toFixed(1) : '--' },
            { label: 'Total Visits', value: data.total_visits },
          ].map((info, i) => (
            <div key={i} className="bg-slate-50 rounded-lg p-2.5 text-center">
              <p className="text-[10px] text-slate-400 uppercase">{info.label}</p>
              <p className="font-bold text-slate-700">{info.value}</p>
            </div>
          ))}
        </div>
        {/* Conditions & Allergies */}
        {(p.chronic_conditions?.length > 0 || p.allergies?.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {p.chronic_conditions?.map((c, i) => (
              <span key={`c-${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200">{c}</span>
            ))}
            {p.allergies?.map((a, i) => (
              <span key={`a-${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">Allergy: {a}</span>
            ))}
          </div>
        )}
      </div>

      {/* Vital Signs */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Vital Signs</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <VitalCard label="Blood Sugar" value={lv?.blood_sugar} unit="mg/dL" icon={Droplets} color="bg-rose-500" trend={vt.blood_sugar} status={bsStatus} testId="vital-blood-sugar" />
          <VitalCard label="Heart Rate" value={lv?.pulse_rate} unit="bpm" icon={Heart} color="bg-red-500" trend={vt.pulse_rate} status={hrStatus} testId="vital-heart-rate" />
          <VitalCard label="Blood Pressure" value={lv ? `${lv.blood_pressure_systolic || '--'}/${lv.blood_pressure_diastolic || '--'}` : null} unit="mmHg" icon={Activity} color="bg-blue-500" trend={vt.bp_systolic} status={bpStatus} testId="vital-bp" />
          <VitalCard label="Temperature" value={lv?.temperature} unit="°C" icon={Thermometer} color="bg-amber-500" trend={vt.temperature} status={tempStatus} testId="vital-temp" />
          <VitalCard label="SpO2" value={lv?.oxygen_saturation} unit="%" icon={Wind} color="bg-teal-500" trend={vt.oxygen_saturation} status={o2Status} testId="vital-spo2" />
        </div>
      </div>

      {/* Body Map + Patient Snapshot Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BodyMap regions={bodyMapData?.regions || {}} />
        <PatientSnapshot
          patient={p}
          latestVitals={lv}
          diagnoses={diagnoses}
          prescriptions={prescriptions}
          upcoming={upcoming_appointments}
          totalVisits={data.total_visits}
          riskData={riskData}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Medical History */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="medical-history">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Stethoscope size={14} /> Medical History</h3>
          {diagnoses?.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {diagnoses.map((d, i) => (
                <div key={i} className="flex items-start justify-between py-2 px-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{d.diagnosis_name}</p>
                    <p className="text-[10px] text-slate-400">Dr. {d.doctor_name} &middot; {d.diagnosis_type}</p>
                  </div>
                  {d.severity && (
                    <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full font-semibold",
                      d.severity === 'severe' ? 'bg-red-100 text-red-700' :
                      d.severity === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                    )}>{d.severity}</span>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-6">No diagnoses recorded</p>}
        </div>

        {/* Lab Reports */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="lab-reports">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><FileText size={14} /> Lab Reports</h3>
          {lab_reports?.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {lab_reports.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-blue-500" />
                    <div>
                      <p className="text-xs font-semibold text-slate-700">{r.report_name || r.test_name || 'Lab Report'}</p>
                      <p className="text-[10px] text-slate-400">{r.created_at?.substring(0, 10)}</p>
                    </div>
                  </div>
                  {r.file_url && (
                    <a href={r.file_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline">View</a>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-6">No reports uploaded</p>}
        </div>

        {/* Risk Factors (moved here per user request) */}
        {riskData?.factors?.length > 0 ? (
          <RiskFactors factors={riskData.factors} />
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><ShieldAlert size={14} /> Risk Factors</h3>
            <p className="text-xs text-slate-400 text-center py-6">No risk factors flagged</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Appointments */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="upcoming-appointments">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><CalendarDays size={14} /> Upcoming</h3>
          {upcoming_appointments?.length > 0 ? (
            <div className="space-y-2">
              {upcoming_appointments.map((a, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{a.appointment_date || a.date} {a.time_slot || a.time || ''}</p>
                    <p className="text-[10px] text-slate-400">Dr. {a.doctor_name}</p>
                  </div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">{a.status}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-4">No upcoming appointments</p>}
        </div>

        {/* Assigned Doctors */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="assigned-doctors">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Stethoscope size={14} /> Assigned Doctors</h3>
          {assigned_doctors?.length > 0 ? (
            <div className="space-y-2">
              {assigned_doctors.map((d, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-3 bg-slate-50 rounded-lg">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                    {d.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{d.name}</p>
                    <p className="text-[10px] text-slate-400">{d.department}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-4">No doctors assigned</p>}
        </div>

        {/* Visit Statistics */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="visit-stats">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Activity size={14} /> Visit Trend</h3>
          {visit_trend?.length > 0 && (
            <div>
              <div className="flex items-end gap-1 h-28">
                {visit_trend.map((d, i) => {
                  const max = Math.max(...visit_trend.map(v => v.visits), 1);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                      <div className="w-full bg-cyan-500 rounded-t-sm transition-all duration-500 hover:bg-cyan-400"
                        style={{ height: `${(d.visits / max) * 100}%`, minHeight: d.visits > 0 ? 4 : 1 }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex mt-1">
                {visit_trend.map((d, i) => <span key={i} className="flex-1 text-center text-[7px] text-slate-400">{d.month}</span>)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Prescriptions */}
      {prescriptions?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="prescriptions">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Pill size={14} /> Recent Prescriptions</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {prescriptions.map((rx, i) => {
              const meds = Array.isArray(rx.medications) ? rx.medications : [];
              const visitDate = rx.visit_date || rx.created_at?.substring(0, 10) || '--';
              return (
                <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid={`rx-${i}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                        <CalendarDays size={11} className="text-slate-400" />
                        Visit date: <span className="text-slate-900">{visitDate}</span>
                      </p>
                      {rx.doctor_name && <p className="text-[10px] text-slate-500 mt-0.5">Prescribed by Dr. {rx.doctor_name}</p>}
                    </div>
                    {rx.follow_up_date && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">Follow-up: {rx.follow_up_date}</span>
                    )}
                  </div>
                  {meds.length > 0 ? (
                    <div className="space-y-1.5">
                      {meds.map((m, mi) => (
                        <div key={mi} className="flex flex-wrap items-baseline gap-2 px-2.5 py-1.5 bg-white rounded-md border border-slate-100">
                          <span className="text-xs font-semibold text-slate-800">{m.name}</span>
                          {m.dosage && <span className="text-[11px] text-slate-600">· {m.dosage}</span>}
                          {m.frequency && <span className="text-[11px] text-slate-600">· {m.frequency}</span>}
                          {m.duration && <span className="text-[11px] text-slate-600">· {m.duration}</span>}
                          {m.route && m.route !== 'oral' && <span className="text-[10px] text-slate-400 italic">({m.route})</span>}
                          {m.instructions && <span className="text-[10px] text-slate-500 w-full italic">{m.instructions}</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic">No medications recorded</p>
                  )}
                  {rx.instructions && (
                    <p className="text-[10px] text-slate-500 mt-2 px-2.5 py-1 bg-amber-50 rounded">
                      <span className="font-semibold text-amber-700">Notes:</span> {rx.instructions}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== OVERVIEW (Admin View) ==========
const PatientOverview = ({ data, onSelectPatient, onRefresh }) => {
  const { kpi, gender, age_groups, departments, recent_patients } = data;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const searchRef = useRef(null);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await patientDashboardAPI.search(q);
      setSearchResults(res.data || []);
    } catch { /* ignore */ }
    setSearching(false);
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const res = await patientDashboardAPI.seedDemoData();
      toast.success(res.data?.message || 'Demo data seeded successfully');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed data');
    }
    setSeeding(false);
  };

  return (
    <div className="space-y-6" data-testid="patient-overview">
      {/* Header + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Patient Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">{data.month} {data.year} &middot; {kpi.total_patients} patient{kpi.total_patients !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={handleSeedData} disabled={seeding}
            className="text-xs gap-1.5" data-testid="seed-demo-data-btn">
            {seeding ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
            {seeding ? 'Seeding...' : 'Seed Demo Data'}
          </Button>
          <div className="relative w-72" ref={searchRef}>
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name, UHID, phone..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
            className="pl-9" data-testid="patient-search" />
          {searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto">
              {searchResults.map((p) => (
                <button key={p.id} onClick={() => { onSelectPatient(p.id); setSearchResults([]); setSearchQuery(''); }}
                  className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left border-b last:border-0" data-testid={`search-result-${p.id}`}>
                  <div className="h-8 w-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 text-xs font-bold">
                    {p.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{p.name}</p>
                    <p className="text-[10px] text-slate-400">{p.patient_id} &middot; {p.age ? `${p.age}y` : ''} {p.gender}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Patients', value: kpi.total_patients, icon: Users, iconBg: 'bg-blue-500', change: kpi.patients_change, sub: `+${kpi.new_this_month} this month` },
          { label: 'Patients in ICU', value: kpi.icu_patients, icon: BedDouble, iconBg: 'bg-red-500', sub: 'Currently admitted' },
          { label: 'Re-Admissions', value: kpi.readmit_count, icon: AlertCircle, iconBg: 'bg-amber-500', sub: 'Multiple admissions' },
          { label: 'Avg Discharge Days', value: kpi.avg_discharge_days, icon: Clock, iconBg: 'bg-teal-500', sub: 'This month' },
        ].map((c, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all" data-testid={`patient-kpi-${i}`}>
            <div className="flex items-start justify-between mb-2">
              <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center shadow-sm", c.iconBg)}>
                <c.icon size={20} className="text-white" />
              </div>
              {c.change !== undefined && c.change !== null && (
                <span className={cn("text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5",
                  c.change >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                )}>
                  {c.change >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
                  {c.change >= 0 ? '+' : ''}{c.change}%
                </span>
              )}
            </div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{c.label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{c.value}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Charts Row: Gender + Age + Department */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gender */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="gender-chart">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Gender Distribution</h3>
          <Donut data={[
            { label: 'Male', value: gender.male, color: '#3b82f6' },
            { label: 'Female', value: gender.female, color: '#ec4899' },
            { label: 'Other', value: gender.other, color: '#94a3b8' },
          ].filter(d => d.value > 0)} />
        </div>

        {/* Age Groups */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="age-chart">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Patients by Age Group</h3>
          <Donut data={age_groups.map((g, i) => ({
            label: g.group,
            value: g.count,
            color: ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'][i % 6],
          })).filter(d => d.value > 0)} size={130} />
        </div>

        {/* Department */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="department-chart">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Patients by Department</h3>
          {departments?.length > 0 ? (
            <HBar data={departments} />
          ) : (
            <p className="text-xs text-slate-400 text-center py-8">No department data</p>
          )}
        </div>
      </div>

      {/* Recent Patients */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="recent-patients">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Recent Patients</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">Patient</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">UHID</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">Age/Gender</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">Phone</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">Registered</th>
                <th className="text-center py-2 px-3 font-semibold text-slate-600 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recent_patients?.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => onSelectPatient(p.id)} data-testid={`recent-patient-${p.id}`}>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
                        {p.name?.charAt(0)}
                      </div>
                      <span className="text-xs font-medium text-slate-700">{p.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-xs text-slate-500 font-mono">{p.patient_id}</td>
                  <td className="py-3 px-3 text-xs text-slate-500">{p.age ? `${p.age}y` : '--'} / {p.gender || '--'}</td>
                  <td className="py-3 px-3 text-xs text-slate-500">{p.phone}</td>
                  <td className="py-3 px-3 text-xs text-slate-400">{typeof p.created_at === 'string' ? p.created_at?.substring(0, 10) : ''}</td>
                  <td className="py-3 px-3 text-center">
                    <Button variant="ghost" size="sm" className="text-xs gap-1 text-blue-600 hover:text-blue-700" data-testid={`view-patient-${p.id}`}>
                      View <ChevronRight size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
              {(!recent_patients || recent_patients.length === 0) && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-xs">No patients registered yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ========== MAIN PAGE ==========
export const PatientDashboardPage = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPatientId, setSelectedPatientId] = useState(null);

  const loadOverview = async () => {
    try {
      const res = await patientDashboardAPI.overview();
      setOverview(res.data);
    } catch { toast.error('Failed to load patient dashboard'); }
    setLoading(false);
  };

  useEffect(() => { loadOverview(); }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="patient-dashboard-loading">
        <div className="h-8 bg-slate-200 rounded w-64 animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (selectedPatientId) {
    return (
      <div data-testid="patient-dashboard-page">
        <Button variant="ghost" size="sm" onClick={() => setSelectedPatientId(null)} className="mb-4 text-xs text-slate-500 gap-1" data-testid="back-to-overview-btn">
          &larr; Back to Patient Dashboard
        </Button>
        <PatientDetail patientId={selectedPatientId} onClose={() => setSelectedPatientId(null)} />
      </div>
    );
  }

  return (
    <div data-testid="patient-dashboard-page">
      {overview && <PatientOverview data={overview} onSelectPatient={setSelectedPatientId} onRefresh={loadOverview} />}
    </div>
  );
};

export default PatientDashboardPage;
